import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ink, inset, semantic, space, typeRole, fontMono, weight } from '@repo/design-tokens';
import { Button, Card, Icon, Tag } from '@repo/design-system';
import { IconDisc, KindGlyph, TagSwitcher } from '@repo/ui';
import type { TagSwitcherOption } from '@repo/ui';

import type { ObserverTargetKind } from '../observerTypes';

/** What the stage says once *Observer still correct* is pressed. */
export const CONFIRMED_TEXT = 'Observer marked still correct';
/** What the stage says when *Add Observer* arms a region (`observer-unified-draw-edit`, R4). */
export const ADD_OBSERVER_ARMED = 'Click the picture to start a region. Esc cancels.';
/** What the stage says when the armed region is cancelled, by Esc or the button again (R4.3). */
export const ADD_OBSERVER_CANCELLED = 'Add Observer cancelled';

/** A chip's value in the switcher: its camera, or `none` for the entity's observers on no camera. */
const chipValue = (endpointId: number | null) =>
  endpointId === null ? 'none' : String(endpointId);

/** One of the entity's cameras, as a chip. `endpointId: null` is its observers on no camera. */
export interface EntityCameraChip {
  endpointId: number | null;
  label: string;
  regionCount: number;
}

export interface EntityCardProps {
  /** The drag handle `MovableCard` hands over, drawn first in the header. */
  grip?: React.ReactNode;
  kind: ObserverTargetKind;
  name: string;
  code?: string;
  /** The observer count line; `null` while the observers read has not answered (no claim made). */
  meta: string | null;
  chips: readonly EntityCameraChip[];
  currentEndpointId: number | null;
  onChip: (endpointId: number | null) => void;
  onSideBySide?: () => void;
  onAddCamera: () => void;
  /**
   * *Add Observer* is offered: drawing is allowed here (`observer-unified-draw-edit`, R4.1), which
   * for a picked entity is always.
   */
  showAddObserver: boolean;
  /** *Add Observer* has armed one region: the button is pressed (R4.2). */
  addObserverArmed: boolean;
  /** *Delete Observer* is offered (the entity has a region on this camera). */
  showDeleteObserver: boolean;
  canDelete: boolean;
  /** Arms one region, or disarms it when pressed again (R4.2, R4.3). */
  onAddObserver: () => void;
  onDeleteObserver: () => void;
  /** The observer strip, where this camera holds two or more of the entity's observers. */
  strip?: React.ReactNode;
  /** The entity needs checking: the sentence, and the confirm (R1). */
  check?: { text: string; onConfirm: () => void };
}

/**
 * The picked entity's card over the picture (`observers-page-camera-stage`, FR8–FR9; v3 states
 * 03, 04 and 06). Its cameras are chips — the current one pressed and ringed, each with its region
 * count — which replace *Change camera*, *Back to all cameras* and *Add another camera*. *Add
 * Observer*, which arms one region, and once a region is on this camera *Delete Observer*. An entity that needs
 * checking says why and offers *Observer still correct* (ruling R1, 2026-10-04).
 *
 * It does not drag itself: `MovableCard` moves it and hands it the `grip`, drawn first in its header
 * (`observers-page-motion`, v3 L1714).
 */
export const EntityCard: React.FC<EntityCardProps> = props => {
  const {
    kind,
    name,
    code,
    meta,
    chips,
    currentEndpointId,
    onChip,
    onSideBySide,
    onAddCamera,
    showAddObserver,
    addObserverArmed,
    showDeleteObserver,
    canDelete,
    onAddObserver,
    onDeleteObserver,
    strip,
    check,
    grip,
  } = props;
  /*
   * *Observer still correct* removes itself when pressed — the entity no longer needs checking — so a
   * keyboard user's focus fell to the page (`onboarding-polish-observer-notices`, AC10). Focus moves
   * on instead: to the card's observer actions where it has them, else to the card itself (it has no
   * actions on a camera that holds none of the entity's regions). The host says the confirm in the
   * stage's one polite region.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const confirmedRef = useRef(false);
  // A confirm belongs to the entity it was pressed on: another entity in this card starts clean.
  useEffect(() => {
    confirmedRef.current = false;
  }, [name, code]);
  useEffect(() => {
    if (!confirmedRef.current) return;
    // Still needing checking — the confirm was refused — so focus stays on the button.
    if (check) {
      confirmedRef.current = false;
      return;
    }
    confirmedRef.current = false;
    const next = actionsRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
    if (next) {
      next.focus();
      return;
    }
    const root = rootRef.current;
    if (root) {
      root.setAttribute('tabindex', '-1');
      root.focus();
    }
  }, [check]);
  const confirm = () => {
    if (!check) return;
    confirmedRef.current = true;
    check.onConfirm();
  };
  const chipOptions: TagSwitcherOption[] = chips.map(chip => ({
    value: chipValue(chip.endpointId),
    text: chip.label,
    icon: 'video',
    ariaLabel: `${chip.label}, ${chip.regionCount} region${chip.regionCount === 1 ? '' : 's'}`,
    trailing: <Count aria-hidden="true">{chip.regionCount}</Count>,
  }));
  return (
    <Root ref={rootRef} surface="on-scene" data-control="">
      <Head>
        {grip}
        {/* The shared disc (floor-plan-page-list-rows): the entity is being edited. */}
        <IconDisc tone="edit" size={30}>
          <KindGlyph kind={kind} />
        </IconDisc>
        <Text>
          <Name>
            <span className="name">{name}</span>
            {code && (
              <Tag surface="on-glass" tone="code">
                {code}
              </Tag>
            )}
          </Name>
          {meta && <Meta>{meta}</Meta>}
        </Text>
      </Head>
      {/* The camera chips as one roving stop (FR8), with the two action chips after them. */}
      <TagSwitcher
        label={`${name}'s cameras`}
        size="dense"
        activation="manual"
        options={chipOptions}
        selectedValue={chipValue(currentEndpointId)}
        onChange={value => {
          const chip = chips.find(c => chipValue(c.endpointId) === value);
          if (chip) onChip(chip.endpointId);
        }}
      >
        {onSideBySide && (
          <Button
            variant="text"
            size="xs"
            iconLeft={<Icon name="table-cells-large" variant="solid" />}
            onClick={onSideBySide}
          >
            Side by Side
          </Button>
        )}
        <Button
          variant="text"
          size="xs"
          iconLeft={<Icon name="plus" variant="solid" />}
          onClick={onAddCamera}
        >
          Add Camera
        </Button>
      </TagSwitcher>
      {strip}
      {check && (
        <Check>
          <span className="check-text">
            <Icon name="circle-exclamation" variant="solid" size="xs" />
            {check.text}
          </span>
          <Button
            variant="frosted"
            size="xs"
            iconLeft={<Icon name="check" variant="solid" />}
            onClick={confirm}
          >
            Observer still correct
          </Button>
        </Check>
      )}
      {(showAddObserver || showDeleteObserver) && (
        <Actions ref={actionsRef}>
          {showAddObserver && (
            <Button
              variant="frosted"
              size="xs"
              iconLeft={<Icon name="plus" variant="solid" />}
              aria-pressed={addObserverArmed}
              onClick={onAddObserver}
            >
              Add Observer
            </Button>
          )}
          {showDeleteObserver && (
            <Button
              variant="destructive"
              size="xs"
              disabled={!canDelete}
              iconLeft={<Icon name="trash-can" />}
              onClick={onDeleteObserver}
            >
              Delete Observer
            </Button>
          )}
        </Actions>
      )}
    </Root>
  );
};

const Root = styled(Card)`
  display: grid;
  gap: ${space[2]};
  max-width: 560px;
  pointer-events: auto;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
`;

const Text = styled.div`
  display: grid;
  gap: ${inset[1]};
  min-width: 0;
`;

const Name = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  ${typeRole.subtitle2};
  color: ${ink[1]};
  .name {
    overflow-wrap: anywhere;
  }
`;

const Meta = styled.span`
  ${typeRole.caption1};
  color: ${ink[3]};
`;

/** A chip's region count, in mono (FR8). */
const Count = styled.span`
  margin-left: ${space[1]};
  font-family: ${fontMono};
  font-weight: ${weight.semibold};
  color: ${semantic.text.secondary};
`;

const Check = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
  .check-text {
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    ${typeRole.caption1};
    color: ${semantic.status.roles.problem.ink};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[2]};
`;
